import { apiService } from '@shared/services/apiServices';
import certificateTemplateEndpoints from './certificateTemplateEndpoints';
import type {
  CertificateTemplateListItem,
  CertificateTemplateListResponse,
  ListCertificateTemplatesParams,
} from '../types';

const buildEmptyResponse = (
  page: number,
  limit: number
): CertificateTemplateListResponse => ({
  data: [],
  pagination: {
    page,
    limit,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
});

export async function listCertificateTemplates(
  params: ListCertificateTemplatesParams = {}
): Promise<CertificateTemplateListResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) {
    queryParams.append('page', String(params.page));
  }
  if (params.limit) {
    queryParams.append('limit', String(params.limit));
  }
  if (params.search) {
    queryParams.append('search', params.search);
  }
  if (params.startDate) {
    queryParams.append('startDate', params.startDate);
  }
  if (params.endDate) {
    queryParams.append('endDate', params.endDate);
  }

  const fallbackPage = params.page ?? 1;
  const fallbackLimit = params.limit ?? 10;
  const qs = queryParams.toString();
  const endpoint = `${certificateTemplateEndpoints.LIST}${qs ? `?${qs}` : ''}`;

  const response = await apiService.getAPI(endpoint);
  if (response.error) {
    return buildEmptyResponse(fallbackPage, fallbackLimit);
  }

  // Defensive parse: tolerate missing/snake_case pagination fields so a
  // partial response from the backend doesn't crash the list page.
  const payload = (response.data ?? {}) as Record<string, unknown>;
  const list = Array.isArray(payload['data'])
    ? (payload['data'] as CertificateTemplateListItem[])
    : [];
  const pagination = (payload['pagination'] ?? payload) as Record<
    string,
    unknown
  >;

  const page = Number(pagination['page'] ?? fallbackPage);
  const limit = Number(pagination['limit'] ?? fallbackLimit);
  const totalCount = Number(
    pagination['totalCount'] ?? pagination['total_count'] ?? list.length
  );
  const totalPages = Number(
    pagination['totalPages'] ??
      pagination['total_pages'] ??
      (limit > 0 ? Math.ceil(totalCount / limit) : 0)
  );
  const hasNextPage =
    typeof pagination['hasNextPage'] === 'boolean'
      ? (pagination['hasNextPage'] as boolean)
      : typeof pagination['has_next_page'] === 'boolean'
        ? (pagination['has_next_page'] as boolean)
        : page < totalPages;
  const hasPreviousPage =
    typeof pagination['hasPreviousPage'] === 'boolean'
      ? (pagination['hasPreviousPage'] as boolean)
      : typeof pagination['has_previous_page'] === 'boolean'
        ? (pagination['has_previous_page'] as boolean)
        : page > 1;

  return {
    data: list,
    pagination: {
      page,
      limit,
      totalCount,
      totalPages,
      hasNextPage,
      hasPreviousPage,
    },
  };
}
